import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  accountGroups,
  tradingAccounts,
  type FundRequest,
  type TradingAccount,
  type TradingAccountCreate,
} from "@/api/tradingAccounts";
import { useAuth } from "@/auth/AuthContext";
import { useActiveAccount } from "@/components/ActiveAccount";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
  Skeleton,
} from "@/components/ui";

/**
 * Trading accounts page — list, create, switch active account, view
 * ledger, fund (admin only).
 */
export function TradingAccountsPage() {
  const { isSuperAdmin } = useAuth();
  const { setActiveId, refresh: refreshActive } = useActiveAccount();
  const qc = useQueryClient();
  const toast = useToast();

  const [openCreate, setOpenCreate] = useState(false);
  const [openFundFor, setOpenFundFor] = useState<TradingAccount | null>(null);
  const [openLedgerFor, setOpenLedgerFor] = useState<TradingAccount | null>(null);
  const [openEditFor, setOpenEditFor] = useState<TradingAccount | null>(null);

  const closeAccount = useMutation({
    mutationFn: (id: string) =>
      tradingAccounts.patch(id, { status: "closed" }),
    onSuccess: (a) => {
      toast.push(`Closed ${a.login_code}`, "warn");
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      refreshActive();
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  const list = useQuery({
    queryKey: ["trading-accounts", "mine"],
    queryFn: () => tradingAccounts.list(true),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["trading-accounts"] });
    refreshActive();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Trading accounts</h1>
        <Button onClick={() => setOpenCreate(true)}>New account</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="space-y-1 p-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : list.data?.length === 0 ? (
            <EmptyState
              title="No trading accounts yet"
              description="Create one to start trading. Each account has its own balance, equity, and margin."
              action={<Button onClick={() => setOpenCreate(true)}>Create account</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium uppercase tracking-wider text-2xs">Login</th>
                  <th className="px-3 py-1.5 text-left font-medium uppercase tracking-wider text-2xs">Label</th>
                  <th className="px-3 py-1.5 text-left font-medium uppercase tracking-wider text-2xs">Currency</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">Balance</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">Equity</th>
                  <th className="px-3 py-1.5 text-right font-medium uppercase tracking-wider text-2xs">Margin used</th>
                  <th className="px-3 py-1.5 text-left font-medium uppercase tracking-wider text-2xs">Status</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((a) => (
                  <tr key={a.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-term-fg">{a.login_code}</td>
                    <td className="px-3 py-1.5 text-term-dim">{a.label ?? "—"}</td>
                    <td className="px-3 py-1.5 text-term-dim">{a.base_currency}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {Number(a.balance).toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">
                      {Number(a.equity).toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {Number(a.margin_used).toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge tone={a.status === "active" ? "good" : a.status === "closed" ? "bad" : "warn"}>
                        {a.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setActiveId(a.id);
                            toast.push(`Active: ${a.login_code}`, "info");
                          }}
                          disabled={a.status !== "active"}
                        >
                          Set active
                        </Button>
                        <Button variant="ghost" onClick={() => setOpenEditFor(a)}>
                          Edit
                        </Button>
                        <Button variant="ghost" onClick={() => setOpenLedgerFor(a)}>
                          Ledger
                        </Button>
                        {isSuperAdmin && (
                          <Button variant="ghost" onClick={() => setOpenFundFor(a)}>
                            Fund
                          </Button>
                        )}
                        {a.status === "active" && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (
                                confirm(
                                  `Close account ${a.login_code}? This stops new orders; existing positions stay until manually closed.`,
                                )
                              ) {
                                closeAccount.mutate(a.id);
                              }
                            }}
                            disabled={closeAccount.isPending}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <CreateAccountModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={refresh} />
      <FundModal account={openFundFor} onClose={() => setOpenFundFor(null)} onFunded={refresh} />
      <LedgerModal account={openLedgerFor} onClose={() => setOpenLedgerFor(null)} />
      <EditAccountModal
        account={openEditFor}
        onClose={() => setOpenEditFor(null)}
        onSaved={refresh}
      />
    </div>
  );
}

function EditAccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: TradingAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [label, setLabel] = useState(account?.label ?? "");
  const [status, setStatus] = useState<TradingAccount["status"]>(
    account?.status ?? "active",
  );
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the modal opens for a different account.
  useState(() => {
    setLabel(account?.label ?? "");
    setStatus(account?.status ?? "active");
  });

  const save = useMutation({
    mutationFn: () => {
      const body: { label?: string; status?: TradingAccount["status"] } = {};
      if ((label.trim() || null) !== (account!.label ?? null)) {
        body.label = label.trim();
      }
      if (status !== account!.status) {
        body.status = status;
      }
      if (Object.keys(body).length === 0) {
        return Promise.reject(new Error("No changes to save."));
      }
      return tradingAccounts.patch(account!.id, body);
    },
    onSuccess: (a) => {
      toast.push(`${a.login_code} updated`, "success");
      onSaved();
      onClose();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  if (!account) return null;
  return (
    <Modal open={true} onClose={onClose} title={`Edit ${account.login_code}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div>
          <Label>Status</Label>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as TradingAccount["status"])}
          >
            <option value="active">active</option>
            <option value="locked">locked — block new orders, keep positions</option>
            <option value="closed">closed — end-of-life</option>
          </Select>
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function CreateAccountModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const groups = useQuery({ queryKey: ["account-groups"], queryFn: () => accountGroups.list() });
  const toast = useToast();
  const [form, setForm] = useState<TradingAccountCreate>({ account_group_id: "" });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: TradingAccountCreate) => tradingAccounts.create(body),
    onSuccess: (acct) => {
      toast.push(`Account ${acct.login_code} created`, "success");
      onCreated();
      onClose();
      setForm({ account_group_id: "" });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open={open} onClose={onClose} title="New trading account">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!form.account_group_id) {
            setError("Pick an account group.");
            return;
          }
          create.mutate(form);
        }}
        className="space-y-3"
      >
        <div>
          <Label>Account group</Label>
          <Select
            value={form.account_group_id}
            onChange={(e) => setForm({ ...form, account_group_id: e.target.value })}
          >
            <option value="">— select —</option>
            {groups.data?.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({g.mode}, {g.default_routing})
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label>Label (optional)</Label>
          <Input
            value={form.label ?? ""}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="My main FX account"
          />
        </div>
        <div>
          <Label>Base currency</Label>
          <Input
            value={form.base_currency ?? ""}
            onChange={(e) => setForm({ ...form, base_currency: e.target.value.toUpperCase() })}
            placeholder="USD"
          />
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function FundModal({
  account,
  onClose,
  onFunded,
}: {
  account: TradingAccount | null;
  onClose: () => void;
  onFunded: () => void;
}) {
  const toast = useToast();
  const [body, setBody] = useState<FundRequest>({ amount: "0", entry_type: "deposit" });
  const [error, setError] = useState<string | null>(null);
  const fund = useMutation({
    mutationFn: () => tradingAccounts.fund(account!.id, body),
    onSuccess: () => {
      toast.push("Account funded", "success");
      onFunded();
      onClose();
      setBody({ amount: "0", entry_type: "deposit" });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });
  if (!account) return null;
  return (
    <Modal open={true} onClose={onClose} title={`Fund ${account.login_code}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          fund.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>Type</Label>
          <Select
            value={body.entry_type}
            onChange={(e) =>
              setBody({ ...body, entry_type: e.target.value as FundRequest["entry_type"] })
            }
          >
            <option value="deposit">deposit</option>
            <option value="withdrawal">withdrawal</option>
            <option value="adjustment">adjustment</option>
          </Select>
        </div>
        <div>
          <Label>Amount ({account.base_currency})</Label>
          <Input
            inputMode="decimal"
            value={String(body.amount)}
            onChange={(e) => setBody({ ...body, amount: e.target.value })}
          />
        </div>
        <div>
          <Label>Note (optional)</Label>
          <Input value={body.note ?? ""} onChange={(e) => setBody({ ...body, note: e.target.value })} />
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={fund.isPending}>
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function LedgerModal({
  account,
  onClose,
}: {
  account: TradingAccount | null;
  onClose: () => void;
}) {
  const ledger = useQuery({
    queryKey: ["trading-accounts", account?.id, "ledger"],
    queryFn: () => tradingAccounts.ledger(account!.id),
    enabled: !!account,
  });
  if (!account) return null;
  return (
    <Modal open={true} onClose={onClose} title={`Ledger — ${account.login_code}`} size="lg">
      {ledger.isLoading ? (
        <p className="text-sm text-term-mute">Loading…</p>
      ) : ledger.data?.length === 0 ? (
        <p className="text-sm text-term-mute">No ledger entries yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-term-mute">
            <tr>
              <th className="px-2 py-1 text-left text-2xs uppercase tracking-wider">When</th>
              <th className="px-2 py-1 text-left text-2xs uppercase tracking-wider">Type</th>
              <th className="px-2 py-1 text-right text-2xs uppercase tracking-wider">Amount</th>
              <th className="px-2 py-1 text-left text-2xs uppercase tracking-wider">Note</th>
            </tr>
          </thead>
          <tbody>
            {ledger.data?.map((row) => (
              <tr key={row.id} className="border-t border-term-border">
                <td className="px-2 py-1 text-term-mute">
                  {new Date(row.occurred_at).toLocaleString()}
                </td>
                <td className="px-2 py-1 text-term-fg">{row.entry_type}</td>
                <td
                  className={`px-2 py-1 text-right tnum ${
                    Number(row.amount) >= 0 ? "up" : "down"
                  }`}
                >
                  {Number(row.amount).toFixed(2)} {row.currency}
                </td>
                <td className="px-2 py-1 text-term-dim">{row.note ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
